import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

export default function Pagination({ page, pageSize, total, onPageChange }: PaginationProps) {
  const { t } = useTranslation();

  if (total <= 0) return null;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, total);
  const canPrev = page > 0;
  const canNext = page < totalPages - 1;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/80">
      <p className="text-sm text-gray-500">
        {t('pagination.showing', { start, end, total })}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!canPrev}
          onClick={() => onPageChange(page - 1)}
          className="btn-secondary px-3 py-1.5 text-sm flex items-center gap-1 disabled:opacity-40"
        >
          <ChevronLeft className="w-4 h-4" />
          {t('pagination.previous')}
        </button>
        <span className="text-sm text-gray-600 min-w-[5rem] text-center">
          {t('pagination.pageOf', { page: page + 1, totalPages })}
        </span>
        <button
          type="button"
          disabled={!canNext}
          onClick={() => onPageChange(page + 1)}
          className="bg-[#1fa8ff] hover:bg-[#0a8cd8] text-white border-0 shadow-sm shadow-[#1fa8ff]/30 font-medium px-4 py-1.5 rounded-lg text-sm flex items-center gap-1 disabled:opacity-40 transition-all"
        >
          {t('pagination.next')}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export const LIST_PAGE_SIZE = 20;
